import {
  Badge,
  HStack,
  Icon,
  Skeleton,
  Text,
  Tooltip,
  usePrefersReducedMotion,
  useToken,
} from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { Globe } from 'lucide-react';
import { TINT_TRANSITION } from './translationsMotion';
import {
  COURSE_TRANSLATION_LANGUAGE_CODES,
  getLanguage,
} from '@duke-hq/shared-constants/courseTranslationLanguages';
import {
  deriveTranslationStatus,
  type ContentTranslationUiStatus,
  type TranslationStatusRow,
} from '@duke-hq/shared-constants/contentTranslationStatus';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import {
  aggregateStatuses,
  coverageColorScheme,
  coverageLabel,
  statusBucket,
  type CoverageStatus,
} from './coverageStatus';

type TranslationRowLike = TranslationStatusRow & { language: string };

export type ResourceCoverageLike = {
  currentPublishedVersionId: string | null;
  rows: TranslationRowLike[];
};

function perLanguageStatuses(
  coverage: ResourceCoverageLike,
  languages: readonly string[],
): { language: string; status: ContentTranslationUiStatus }[] {
  const rowByLanguage = new Map(coverage.rows.map((row) => [row.language, row]));
  return languages.map((language) => ({
    language,
    status: deriveTranslationStatus(
      rowByLanguage.get(language),
      coverage.currentPublishedVersionId,
    ),
  }));
}

// Null when unpublished, so the caller hides the badge.
export function aggregateResourceStatus(
  coverage: ResourceCoverageLike,
  languages: readonly string[] = COURSE_TRANSLATION_LANGUAGE_CODES,
): CoverageStatus | null {
  if (coverage.currentPublishedVersionId === null) {
    return null;
  }
  return aggregateStatuses(perLanguageStatuses(coverage, languages).map((entry) => entry.status));
}

// Rolls a unit's (or course's) published resources into one state: every
// (resource x language) cell through the same ladder. Null when none published.
export function aggregateUnitStatus(
  resources: ResourceCoverageLike[],
  languages: readonly string[] = COURSE_TRANSLATION_LANGUAGE_CODES,
): CoverageStatus | null {
  const published = resources.filter((resource) => resource.currentPublishedVersionId !== null);
  if (published.length === 0) {
    return null;
  }
  const statuses = published.flatMap((resource) =>
    perLanguageStatuses(resource, languages).map((entry) => entry.status),
  );
  return aggregateStatuses(statuses);
}

// The denominator, named in the tooltip. `course`: the course's configured set.
// `all-languages`: every translatable language, for course-agnostic surfaces like
// the unit page (a unit is shared across courses).
export type TranslationCoverageScope = 'course' | 'all-languages';

function scopeSentence(scope: TranslationCoverageScope, ready: number, total: number): string {
  return scope === 'course'
    ? t`Translated into ${ready} of ${total} languages this course offers.`
    : t`Translated into ${ready} of ${total} languages.`;
}

// Progress sentence, then languages grouped by state, so the detail shows on
// hover without opening the drawer.
function tooltipFor(
  coverage: ResourceCoverageLike,
  languages: readonly string[],
  scope: TranslationCoverageScope,
): string {
  const ready: string[] = [];
  const gaps: string[] = [];
  const attention: string[] = [];
  const pending: string[] = [];

  for (const entry of perLanguageStatuses(coverage, languages)) {
    const name = getLanguage(entry.language)?.englishName ?? entry.language;
    // Groups failed+stale as "needs attention" and omits not-translated. A
    // partly-in-English language is ready but named separately, not as done.
    if (entry.status === 'available-partial') {
      gaps.push(name);
      continue;
    }
    const bucket = statusBucket(entry.status);
    if (bucket === 'ready') {
      ready.push(name);
    } else if (bucket === 'failed' || bucket === 'stale') {
      attention.push(name);
    } else if (bucket === 'inProgress') {
      pending.push(name);
    }
  }

  const parts: string[] = [scopeSentence(scope, ready.length + gaps.length, languages.length)];
  if (ready.length > 0) {
    parts.push(t`Translated: ${ready.join(', ')}`);
  }
  if (gaps.length > 0) {
    parts.push(t`Partly in English: ${gaps.join(', ')}`);
  }
  if (pending.length > 0) {
    parts.push(t`Translating: ${pending.join(', ')}`);
  }
  if (attention.length > 0) {
    parts.push(t`Needs review: ${attention.join(', ')}`);
  }
  return parts.join(' · ');
}

// A globe + worded state in a quiet pill, tinted only for attention states so it
// never out-shouts the publish badge beside it. Both resource and unit badges
// render through this, so they read identically.
//
// Tint comes from resolved ramp tokens, not `colorScheme`: a class swap can't be
// transitioned, so this lets a live badge (blue "Translating 3/16" landing green)
// bleed along the ramp instead of snapping. Same trick as TranslationStatusChip.
function CoverageBadgeView({
  status,
  tooltip,
  'data-testid': dataTestId,
}: {
  status: CoverageStatus;
  tooltip: string;
  'data-testid'?: string;
}) {
  const motionDisabled = usePrefersReducedMotion();
  const scheme = coverageColorScheme(status);
  const [bg, color] = useToken('colors', [`${scheme}.50`, `${scheme}.700`]);

  return (
    <Tooltip label={tooltip}>
      <Badge
        variant="pill"
        fontSize="xs"
        fontWeight="semibold"
        cursor="default"
        bg={bg}
        color={color}
        transition={motionDisabled ? 'none' : TINT_TRANSITION}
        data-testid={dataTestId}
      >
        <HStack spacing="1.5" as="span">
          <Icon as={Globe} boxSize={iconSize.xs} aria-hidden />
          {/* tabular-nums so a ticking "Translating 3/16" advances without the
              pill jittering in width per digit. */}
          <Text as="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {coverageLabel(status)}
          </Text>
        </HStack>
      </Badge>
    </Tooltip>
  );
}

// Holds a row's place while coverage loads (rather than flashing "Not
// translated"), sized to the badge to avoid layout shift on swap.
export function TranslationCoverageBadgeSkeleton({
  'data-testid': dataTestId,
}: {
  'data-testid'?: string;
}) {
  return (
    <Skeleton
      h={pxToRem(20)}
      w={pxToRem(96)}
      borderRadius="full"
      data-testid={dataTestId}
      aria-label={t`Loading translation status`}
    />
  );
}

// Row-level indicator beside the publish badge where a single resource is listed;
// per-language breakdown is on the tooltip. Nothing for an unpublished resource
// (translation is downstream of publishing).
export function TranslationCoverageBadge({
  coverage,
  languages = COURSE_TRANSLATION_LANGUAGE_CODES,
  scope = 'all-languages',
  'data-testid': dataTestId,
}: {
  coverage: ResourceCoverageLike;
  // Defaults to every translatable language; course-scoped surfaces pass the
  // course's configured set.
  languages?: readonly string[];
  scope?: TranslationCoverageScope;
  'data-testid'?: string;
}) {
  const status = aggregateResourceStatus(coverage, languages);
  if (status === null) {
    return null;
  }
  return (
    <CoverageBadgeView
      status={status}
      tooltip={tooltipFor(coverage, languages, scope)}
      data-testid={dataTestId}
    />
  );
}

// Unit-level badge for a course's units list: the resource badge's visual, rolled
// up across the unit's published resources.
export function UnitCoverageBadge({
  resources,
  languages = COURSE_TRANSLATION_LANGUAGE_CODES,
  showUnpublishedAsNotTranslated = false,
  'data-testid': dataTestId,
}: {
  resources: ResourceCoverageLike[];
  languages?: readonly string[];
  // A unit with nothing published rolls up to null (translation is downstream of
  // publishing). Most surfaces hide the badge; list columns pass true so a blank
  // cell doesn't read as broken, showing "Not translated" instead.
  showUnpublishedAsNotTranslated?: boolean;
  'data-testid'?: string;
}) {
  const status = aggregateUnitStatus(resources, languages);
  if (status === null) {
    if (showUnpublishedAsNotTranslated) {
      const none: CoverageStatus = { kind: 'none' };
      return (
        <CoverageBadgeView
          status={none}
          tooltip={rollupTooltipFor(none, 'unit')}
          data-testid={dataTestId}
        />
      );
    }
    return null;
  }
  return (
    <CoverageBadgeView
      status={status}
      tooltip={rollupTooltipFor(status, 'unit')}
      data-testid={dataTestId}
    />
  );
}

// A per-language breakdown isn't meaningful at a rollup level, so the tooltip
// states the aggregate in words, scoped to the unit or the whole course.
function rollupTooltipFor(status: CoverageStatus, scope: 'unit' | 'course'): string {
  if (scope === 'course') {
    switch (status.kind) {
      case 'failed':
        return t`${status.count} resources failed to translate in this course`;
      case 'stale':
        return t`Some translations in this course need review`;
      case 'in-progress':
        return t`This course's translations are being generated`;
      case 'complete':
        return t`Every resource in this course is translated`;
      case 'gaps':
        return t`Every resource in this course is translated, ${status.count} partly in English`;
      case 'partial':
        return t`Some resources in this course are translated`;
      case 'none':
        return t`No resources in this course are translated yet`;
    }
  }
  switch (status.kind) {
    case 'failed':
      return t`${status.count} resources failed to translate in this unit`;
    case 'stale':
      return t`Some translations in this unit need review`;
    case 'in-progress':
      return t`This unit's translations are being generated`;
    case 'complete':
      return t`Every resource in this unit is translated`;
    case 'gaps':
      return t`Every resource in this unit is translated, ${status.count} partly in English`;
    case 'partial':
      return t`Some resources in this unit are translated`;
    case 'none':
      return t`No resources in this unit are translated yet`;
  }
}

// Course-wide rollup in the standard pill style (globe + status word), the same
// visual the resource and unit badges use. The muted `CourseCoverageBadge` below
// is the courses-table variant; this is for the course editor, where the full
// status nuance is wanted.
export function CourseCoverageBadgeDetailed({
  resources,
  languages = COURSE_TRANSLATION_LANGUAGE_CODES,
  'data-testid': dataTestId,
}: {
  resources: ResourceCoverageLike[];
  languages?: readonly string[];
  'data-testid'?: string;
}) {
  const status = aggregateUnitStatus(resources, languages);
  if (status === null) {
    return null;
  }
  return (
    <CoverageBadgeView
      status={status}
      tooltip={rollupTooltipFor(status, 'course')}
      data-testid={dataTestId}
    />
  );
}

// A scanned courses table needs only done / partly done / not started, not the
// failed/stale/in-flight nuance (that's the course editor's job). The middle
// bucket reads as "partly translated", since it mostly folds resting-partial
// courses rather than ones with work actively running.
type CourseCoverageState = 'translated' | 'partial' | 'failed' | 'not-translated';

function courseStateFrom(status: CoverageStatus): CourseCoverageState {
  switch (status.kind) {
    case 'complete':
      return 'translated';
    case 'none':
      return 'not-translated';
    // A failure needs attention: a course with failed translations must not read
    // as green "partly translated". In-progress and stale still read as partly done,
    // and so does a fully-translated course with English gaps.
    case 'failed':
      return 'failed';
    case 'gaps':
    case 'partial':
    case 'in-progress':
    case 'stale':
      return 'partial';
  }
}

// Course-level badge for the courses list, beside the table's other muted pills.
// A muted pill (green done / green partly / red failed / grey not started); the
// per-language detail lives in the course editor.
export function CourseCoverageBadge({
  resources,
  languages = COURSE_TRANSLATION_LANGUAGE_CODES,
  'data-testid': dataTestId,
}: {
  resources: ResourceCoverageLike[];
  languages?: readonly string[];
  'data-testid'?: string;
}) {
  const status = aggregateUnitStatus(resources, languages);
  if (status === null) {
    return null;
  }
  const state = courseStateFrom(status);
  const { colorScheme, label } = {
    translated: { colorScheme: 'green', label: t`Translated` },
    partial: { colorScheme: 'green', label: t`Partly translated` },
    failed: { colorScheme: 'red', label: t`Translations failed` },
    'not-translated': { colorScheme: 'gray', label: t`Not translated` },
  }[state];

  return (
    <Badge
      borderRadius="full"
      colorScheme={colorScheme}
      variant="muted"
      textTransform="none"
      fontWeight="medium"
      cursor="default"
      data-testid={dataTestId}
    >
      <HStack spacing="1" as="span">
        <Icon as={Globe} boxSize={iconSize.xs} aria-hidden />
        <Text as="span">{label}</Text>
      </HStack>
    </Badge>
  );
}
